import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import { UsersListSchema } from "./types";
import { Route as userListRoute } from "../index";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import getPharmacyID from "@/utils/pharmacy_code";
import { useRouter } from "@tanstack/react-router";
import { toast } from "@/components/ui/use-toast";
import { httpClient } from "@/httpClient";
const AddMember = () => {
  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [username, setUsername] = useState("");
  const [userRoles, setUserRoles] = useState({
    admin: false,
    superadmin: false,
    employee: false,
    inventory: false,
    sales: false,
  });
  const router = useRouter()
  return (
    <Dialog>
      <DialogTrigger>
        <Button variant="outline">Add User</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New User</DialogTitle>
          <DialogDescription>
            Fill in the details to add a new user to your team.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="username">Username</Label>
          <Input
            type="text"
            placeholder="Enter username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            type="email"
            placeholder="Enter user email"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="first-name">First Name</Label>
          <Input
            type="text"
            id="first-name"
            placeholder="Enter user first name"
            value={userFirstName}
            onChange={(e) => setUserFirstName(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="last-name">Last Name</Label>
          <Input
            type="text"
            id="last-name"
            placeholder="Enter user last name"
            value={userLastName}
            onChange={(e) => setUserLastName(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="role">Roles</Label>
          <Label className="flex items-center space-x-2">
            <Checkbox id="admin" checked={userRoles.admin} onCheckedChange={() => setUserRoles({ ...userRoles, admin: !userRoles.admin })} />
            <span>Admin</span>
          </Label>
          <Label className="flex items-center space-x-2">
            <Checkbox id="superadmin" checked={userRoles.superadmin} onCheckedChange={() => setUserRoles({ ...userRoles, superadmin: !userRoles.superadmin })} />
            <span>Superadmin</span>
          </Label>
          <Label className="flex items-center space-x-2">
            <Checkbox id="employee" checked={userRoles.employee} onCheckedChange={() => setUserRoles({ ...userRoles, employee: !userRoles.employee })} />
            <span>Employee</span>
          </Label>
          <Label className="flex items-center space-x-2">
            <Checkbox id="inventory" checked={userRoles.inventory} onCheckedChange={() => setUserRoles({ ...userRoles, inventory: !userRoles.inventory })} />
            <span>Inventory</span>
          </Label>
          <Label className="flex items-center space-x-2">
            <Checkbox id="sales" checked={userRoles.sales} onCheckedChange={() => setUserRoles({ ...userRoles, sales: !userRoles.sales })} />
            <span>Sales</span>
          </Label>
        </div>
        <DialogFooter>
          <Button
            type="submit"
            onClick={async () => {
              const newUser = {
                username: username,
                first_name: userFirstName,
                last_name: userLastName,
                email: userEmail,
                pharmacy_code: getPharmacyID(),
                roles: Object.keys(userRoles).filter(role => userRoles[role as keyof typeof userRoles]),
              };
              // Call API to add user
              try {
                await httpClient().post(`${import.meta.env.VITE_API_URL as string}/public/pharmacy/users/create`, newUser);
                router.invalidate();
                setUserFirstName("");
                setUserLastName("");
                setUserEmail("");
                setUsername("");
                setUserRoles({
                  admin: false,
                  superadmin: false,
                  employee: false,
                  inventory: false,
                  sales: false,
                });
                toast({
                  title: "User Added",
                  description: "The user has been successfully added. Default Password is `Neulink@123`",
                  variant: "success",
                  duration: 10000,
                });
              } catch (error) {
                console.error("Failed to add user:", error);
              }
            }}
          >
            Add User
          </Button>
          <Button type="button" variant="outline" onClick={() => {
            setUserFirstName("");
            setUserLastName("");
            setUserEmail("");
            setUserRoles({
              admin: false,
              superadmin: false,
              employee: false,
              inventory: false,
              sales: false,
            });
          }}>
            Reset
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Manage = () => {
  const data = userListRoute.useLoaderData();
  const [error, setError] = useState<string | null>(null);

  const user = localStorage.getItem("user");

  return (
    <>
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Manage Members</h2>
          <p className="text-muted-foreground">
            Invite your team members to collaborate.
          </p>
        </div>
        {user && (JSON.parse(user).roles.includes("admin") || JSON.parse(user).roles.includes("superadmin")) && <AddMember />}
      </div>
      <hr className="my-4" />
      {error && <p className="text-red-500">{error}</p>}
      <div className="flex gap-x-4">
        <div className="flex-1 flex flex-col gap-y-4">
          {data.users.map((user) => (
            <div key={user.id} className="flex justify-between items-center">
              <div>
                <p className="text-gray-700">
                  {user.first_name} {user.last_name}
                </p>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>
              <div className="flex divide-x">
                {user.roles.map((role) => (
                  <span key={role} className="px-2 text-xs text-gray-600">
                    {role}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

      </div>
    </>
  );
};

export default Manage;
